import {
    AuthorizationInfo,
    defaultServiceTypesByAuthTier1,
    isAssessmentAuth,
    YesNoOption,
} from "../../domain";
import {TreatmentHoldInput} from "./TreatmentHoldFormModal";
import {toDateOnlyString} from "common/util/date";

export type TreatmentHoldSubmitInput = Omit<TreatmentHoldInput, "canProviderOfferFso" | "providerResumeServices"> & {
    /** null when canProviderResumeServices is NO — GraphQL enum cannot accept '' */
    providerResumeServices: string | null;
};

export function emptyTreatmentHoldInput(authorization?: AuthorizationInfo): TreatmentHoldInput {
    return {
        holdType: "",
        requestReason: "",
        requestReasonOther: "",
        dateHoldDiscussedWithFamily: "",
        startDate: "",
        endDate: "",
        comments: "",
        canProviderResumeServices: "",
        canProviderOfferFso: "",
        providerResumeServices: "",
        familyNotifiedUnableToResume: null,
        dateOfLastTreatment: null,
        dateOfMostRecentAppointment: null,
        dateOfLastFullScheduleOffer: null,
        offeredParaprofessionalHours: null,
        offeredMidLevelHours: null,
        offeredHighLevelHours: null,
        offeredGroupCareHours: null,
        serviceType: authorization ? defaultServiceTypesByAuthTier1(authorization) : [],
        providerRequestForCcsConsultation: null,
        reasonForConsultation: null,
        summaryOfStepsTaken: null,
        providerContactName: null,
        providerContactEmail: null,
        providerContactPhone: null,
    };
}

type TreatmentHoldApiPartial = Partial<TreatmentHoldInput> & {
    serviceType?: readonly (string | null | undefined)[] | null;
};

function normalizeServiceTypes(serviceType?: readonly (string | null | undefined)[] | null): string[] {
    return (serviceType ?? []).filter((s): s is string => s != null && s !== "");
}

export function fromApiTreatmentHold(data: TreatmentHoldApiPartial): Partial<TreatmentHoldInput> {
    const canResume = data.canProviderResumeServices;
    let canProviderOfferFso = "";
    if (canResume === YesNoOption.YES || canResume === "Yes") {
        canProviderOfferFso = data.providerResumeServices === "Yes" ? YesNoOption.YES : YesNoOption.NO;
    }
    const apiServiceTypes = normalizeServiceTypes(data.serviceType);
    return {
        holdType: data.holdType ?? "",
        requestReason: data.requestReason ?? "",
        requestReasonOther: data.requestReasonOther ?? "",
        dateHoldDiscussedWithFamily: data.dateHoldDiscussedWithFamily ?? "",
        startDate: data.startDate ?? "",
        endDate: data.endDate ?? "",
        comments: data.comments ?? "",
        canProviderResumeServices: data.canProviderResumeServices ?? "",
        providerResumeServices: data.providerResumeServices ?? "",
        familyNotifiedUnableToResume: data.familyNotifiedUnableToResume ?? null,
        dateOfLastTreatment: data.dateOfLastTreatment ?? null,
        dateOfMostRecentAppointment: data.dateOfMostRecentAppointment ?? null,
        dateOfLastFullScheduleOffer: data.dateOfLastFullScheduleOffer ?? null,
        offeredParaprofessionalHours: data.offeredParaprofessionalHours ?? null,
        offeredMidLevelHours: data.offeredMidLevelHours ?? null,
        offeredHighLevelHours: data.offeredHighLevelHours ?? null,
        offeredGroupCareHours: data.offeredGroupCareHours ?? null,
        ...(apiServiceTypes.length > 0 ? {serviceType: apiServiceTypes} : {}),
        providerRequestForCcsConsultation: data.providerRequestForCcsConsultation ?? null,
        reasonForConsultation: data.reasonForConsultation ?? null,
        summaryOfStepsTaken: data.summaryOfStepsTaken ?? null,
        providerContactName: data.providerContactName ?? null,
        providerContactEmail: data.providerContactEmail ?? null,
        providerContactPhone: data.providerContactPhone ?? null,
        canProviderOfferFso,
    };
}

export function toTreatmentHoldSubmitPayload(
    data: TreatmentHoldInput | TreatmentHoldSubmitInput,
    authorization?: AuthorizationInfo,
): TreatmentHoldSubmitInput {
    const uiCanOfferFso = "canProviderOfferFso" in data ? data.canProviderOfferFso : undefined;
    const canResume = data.canProviderResumeServices === YesNoOption.YES || data.canProviderResumeServices === "Yes";
    const canOfferFso =
        uiCanOfferFso === YesNoOption.YES || uiCanOfferFso === "Yes"
        || ((uiCanOfferFso == null || uiCanOfferFso === "") && data.providerResumeServices === "Yes");
    const includeOfferFields = canResume && canOfferFso && !isAssessmentAuth(authorization);
    return {
        holdType: data.holdType,
        requestReason: data.requestReason,
        requestReasonOther: data.requestReasonOther,
        dateHoldDiscussedWithFamily: toDateOnlyString(data.dateHoldDiscussedWithFamily) ?? "",
        startDate: toDateOnlyString(data.startDate) ?? "",
        endDate: toDateOnlyString(data.endDate) ?? "",
        comments: data.comments,
        canProviderResumeServices: normalizeYesNo(data.canProviderResumeServices) ?? "",
        providerResumeServices: canResume ? (canOfferFso ? "Yes" : "No") : null,
        familyNotifiedUnableToResume: canResume ? null : normalizeYesNo(data.familyNotifiedUnableToResume),
        dateOfLastTreatment: canResume ? null : toDateOnlyString(data.dateOfLastTreatment),
        dateOfMostRecentAppointment: null,
        dateOfLastFullScheduleOffer: canResume ? null : toDateOnlyString(data.dateOfLastFullScheduleOffer),
        offeredParaprofessionalHours: includeOfferFields ? data.offeredParaprofessionalHours : null,
        offeredMidLevelHours: includeOfferFields ? data.offeredMidLevelHours : null,
        offeredHighLevelHours: includeOfferFields ? data.offeredHighLevelHours : null,
        offeredGroupCareHours: includeOfferFields ? data.offeredGroupCareHours : null,
        serviceType: includeOfferFields ? data.serviceType : [],
        providerRequestForCcsConsultation: normalizeYesNo(data.providerRequestForCcsConsultation),
        reasonForConsultation: data.providerRequestForCcsConsultation === YesNoOption.YES ? data.reasonForConsultation : null,
        summaryOfStepsTaken: data.providerRequestForCcsConsultation === YesNoOption.YES ? data.summaryOfStepsTaken : null,
        providerContactName: data.providerRequestForCcsConsultation === YesNoOption.YES ? data.providerContactName : null,
        providerContactEmail: data.providerRequestForCcsConsultation === YesNoOption.YES ? data.providerContactEmail : null,
        providerContactPhone: data.providerRequestForCcsConsultation === YesNoOption.YES ? data.providerContactPhone : null,
    };
}

function normalizeYesNo(value: string | null): string | null {
    if (value == null || value === "") {
        return null;
    }
    if (value === "Yes") {
        return YesNoOption.YES;
    }
    if (value === "No") {
        return YesNoOption.NO;
    }
    return value;
}

export function shouldRedirectToStaffingPlan(
    data: TreatmentHoldInput,
    authorization?: AuthorizationInfo,
): boolean {
    if (isAssessmentAuth(authorization)) {
        return false;
    }
    const canResume = data.canProviderResumeServices === YesNoOption.YES || data.canProviderResumeServices === "Yes";
    const cannotOfferFso = data.canProviderOfferFso === YesNoOption.NO || data.canProviderOfferFso === "No";
    return canResume && cannotOfferFso;
}
